import { useRef, useState } from "react";
import { Paperclip, X, Loader2, FileText, ShieldAlert } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useLocale } from "@/context/LocaleContext";

const ACCEPT = ".pdf,.doc,.docx,.txt,.md,.csv";
const MAX = 3;

export function AttachmentUploader({ files, onChange, disabled }) {
  const { t } = useLocale();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const pick = () => {
    if (!confirmed) return;
    if (files.length >= MAX) return toast.error(t("Du kan vedhæfte op til {n} dokumenter", { n: MAX }));
    inputRef.current?.click();
  };

  const onFiles = async (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = "";
    if (!chosen.length) return;
    const room = MAX - files.length;
    if (chosen.length > room) toast.error(`Maks ${MAX} dokumenter – tager de første ${room}`);
    const batch = chosen.slice(0, Math.max(0, room));
    setUploading(true);
    for (const f of batch) {
      if (f.size > 20 * 1024 * 1024) { toast.error(`${f.name} er for stor (maks 20 MB)`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/documents/upload", fd);
        const doc = data.document;
        onChange((prev) => [...prev, { id: doc.id, name: doc.original_filename }]);
      } catch (err) {
        toast.error(apiError(err));
      }
    }
    setUploading(false);
  };

  const remove = (id) => onChange((prev) => prev.filter((x) => x.id !== id));

  return (
    <div data-testid="attachment-uploader">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={pick} disabled={disabled || uploading || !confirmed} data-testid="attach-btn"
          className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-foreground/30 text-foreground font-medium hover:border-foreground/60 hover:bg-secondary transition-colors disabled:opacity-70 disabled:cursor-not-allowed">
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
          {t("Vedhæft dokument")}
        </button>
        {files.map((f) => (
          <span key={f.id} data-testid="attach-chip"
            className="inline-flex items-center gap-1.5 text-xs pl-2.5 pr-1.5 py-1.5 rounded-md bg-secondary text-foreground max-w-[220px]">
            <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate">{f.name}</span>
            <button type="button" onClick={() => remove(f.id)} data-testid="attach-remove"
              className="shrink-0 text-muted-foreground hover:text-foreground" aria-label="Fjern">
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
        {files.length === 0 && !uploading && (
          <span className="text-xs text-foreground/70">{t("PDF, Word eller tekst – bruges som ekstra kontekst")}</span>
        )}
        <input ref={inputRef} type="file" accept={ACCEPT} multiple hidden onChange={onFiles} data-testid="attach-input" />
      </div>
      <p className="flex items-start gap-1.5 text-xs text-muted-foreground mt-2" data-testid="attachment-sensitive-warning">
        <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" strokeWidth={1.75} />
        {t("Undlad at uploade følsomme personoplysninger (fx helbred, CPR-numre, race eller religion). Du er selv dataansvarlig for det, du uploader. Indhold behandles af vores AI-leverandør OpenAI og kan opbevares dér i op til 30 dage. Det bruges kun til at levere jeres egne resultater – aldrig til at træne tredjeparters AI-modeller.")}
      </p>
      <label className="flex items-start gap-2 text-xs text-foreground mt-2 cursor-pointer select-none" data-testid="attachment-confirm-label">
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}
          data-testid="attachment-confirm-checkbox"
          className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-border accent-foreground cursor-pointer" />
        <span>{t("Jeg bekræfter, at det uploadede materiale ikke indeholder personfølsomme oplysninger.")}</span>
      </label>
    </div>
  );
}
